import { UserProfileRole } from '@@types/api.types';
import { AttachmentCategory, type AttachmentSelectDto, AttachmentType } from '@@types/api.types';
import { AttachmentsList } from '@components/shared/attachments/attachments-list.component';
import { AttachmentsModal } from '@components/shared/attachments/attachments-modal.component';
import { FormLabel } from '@components/shared/form-label.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import { useGetTechnologyAttachments } from '@queries/attachments.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { formatTimelineDate } from '@utils/assessment.util';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { EditIcon } from 'lucide-react';
import { FunctionComponent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Steps, TechnologyTechnologyModal } from './technology-technology-modal.component';

type TechnologyReferencesTileProps = {
  className?: string;
  technologyId?: string;
  title?: string;
};

export const TechnologyReferencesTile: FunctionComponent<TechnologyReferencesTileProps> = (props) => {
  const { technologyId: technologyIdFromParams } = useParams();
  const { technologyId, className, title = 'Research' } = props;
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();

  const resolvedTechnologyId = technologyId ?? technologyIdFromParams;
  const { data: technology, isLoading: isTechnologyPending } = useGetTechnology(resolvedTechnologyId);
  const { data: attachments = [], isPending: isAttachmentPending } = useGetTechnologyAttachments({
    category: AttachmentCategory.RESEARCH_ARTEFACT,
    technologyId: resolvedTechnologyId ?? '',
  });

  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isTechnologyModalOpen, setIsTechnologyModalOpen] = useState(false);
  const [attachmentToEdit, setAttachmentToEdit] = useState<AttachmentSelectDto | null>(null);

  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = user?.profile?.role === UserProfileRole.INNOVATOR && !isChildOrganizationView;

  const researchKeywords = [...(technology?.metadata?.researchKeywords || [])].sort((a, b) => a.localeCompare(b)) || [];

  const hasReferences = attachments.length > 0;
  const hasResearchKeywords = researchKeywords.length > 0;
  const hasTimeline = Boolean(
    technology?.timeline?.startDate || (technology?.timeline?.startMonth && technology?.timeline?.startYear),
  );

  const isEmpty = !hasReferences && !hasResearchKeywords && !hasTimeline;

  const handleOpenAttachmentModal = (attachment?: AttachmentSelectDto) => {
    setAttachmentToEdit(attachment ?? null);
    setIsAttachmentModalOpen(true);
  };

  const handleCloseAttachmentModal = () => {
    setAttachmentToEdit(null);
    setIsAttachmentModalOpen(false);
  };

  const handleOpenTechnologyModal = () => {
    setIsTechnologyModalOpen(true);
  };

  const handleCloseTechnologyModal = () => {
    setIsTechnologyModalOpen(false);
  };

  return (
    <>
      <Tile
        className={cn('w-full', (isEmpty || isTechnologyPending) && 'min-h-[200px]', className)}
        emptyState={{ title: 'Technology Research Unavailable' }}
        header={{
          iconButton: canEdit
            ? {
                icon: <EditIcon size={16} />,
                onClick: handleOpenTechnologyModal,
              }
            : undefined,
          title,
        }}
        hoverReveal={canEdit}
        isEmpty={isEmpty}
        isLoading={isTechnologyPending || isAttachmentPending}
      >
        <div className="flex flex-col gap-4">
          <AttachmentsList
            attachments={attachments}
            onEdit={canEdit ? handleOpenAttachmentModal : undefined}
            showHeader={true}
            showInAccordion={false}
            title="Research Artefacts"
            useFormLabel={true}
          />

          {researchKeywords && researchKeywords.length > 0 && (
            <section className="flex flex-col gap-2">
              <FormLabel label="Research & Development Keywords" />
              <div className="flex gap-1 flex-wrap">
                {researchKeywords.map((keyword) => (
                  <Badge
                    variant="outline"
                    key={keyword}
                    size="xs"
                  >
                    {keyword}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          {hasTimeline && technology?.timeline && (
            <div className="flex flex-col gap-2">
              <section className="flex gap-1">
                <FormLabel label="Development Started:" />
                <div className="flex flex-col gap-3">
                  <div className="text-sm text-gray-600">{formatTimelineDate(technology.timeline)}</div>
                </div>
              </section>

              <section className="flex gap-1">
                <FormLabel label="Research Validated:" />
                <div className="flex flex-col gap-3">
                  <div className="text-sm text-gray-600">{technology?.timeline?.validated ? 'Yes' : 'No'}</div>
                </div>
              </section>

              {technology?.timeline?.description && (
                <section className="flex flex-col gap-1">
                  <FormLabel label="Validation Details:" />
                  <div className="text-xs text-gray-600 whitespace-pre-wrap">{technology?.timeline?.description}</div>
                </section>
              )}
            </div>
          )}
        </div>
      </Tile>
      {resolvedTechnologyId && canEdit && (
        <AttachmentsModal
          attachmentToEdit={attachmentToEdit}
          category={AttachmentCategory.RESEARCH_ARTEFACT}
          defaultType={AttachmentType.URL}
          isOpen={isAttachmentModalOpen}
          onClose={handleCloseAttachmentModal}
          targetId={resolvedTechnologyId}
          targetType="technology"
        />
      )}
      {technology && canEdit && (
        <TechnologyTechnologyModal
          initialStep={Steps.RESEARCH}
          isEdit={true}
          isOpen={isTechnologyModalOpen}
          onClose={handleCloseTechnologyModal}
          technology={technology}
        />
      )}
    </>
  );
};
